import React, { useState, useEffect, useRef, useMemo } from 'react';
import { AnimationConfig, DeviceAnimationOverride } from '../../types/animation';
import { validateAnimationConfig, getPresetCss } from '../../utils/animationEngine';

interface AnimationWrapperProps {
  config?: AnimationConfig | null;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  isEditor?: boolean;
  forcePlayKey?: number | string;
  deviceMode?: 'desktop' | 'tablet' | 'mobile';
  as?: React.ElementType;
  [key: string]: any;
}

export const AnimationWrapper: React.FC<AnimationWrapperProps> = ({
  config,
  children,
  className = '',
  style = {},
  isEditor = false,
  forcePlayKey,
  deviceMode = 'desktop',
  as: Component = 'div',
  ...restProps
}) => {
  const containerRef = useRef<HTMLElement | null>(null);
  const [isInView, setIsInView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Validate configuration
  const validConfig = useMemo(() => {
    return validateAnimationConfig(config);
  }, [config]);

  // Check prefers-reduced-motion
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const listener = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }
  }, []);

  // Determine if animation is enabled for this device
  const isDeviceEnabled = useMemo(() => {
    if (!validConfig.enabled) return false;
    if (prefersReducedMotion) return false;

    if (deviceMode === 'mobile') return validConfig.mobileEnabled !== false;
    if (deviceMode === 'tablet') return validConfig.tabletEnabled !== false;
    return validConfig.desktopEnabled !== false;
  }, [validConfig, deviceMode, prefersReducedMotion]);

  // Active settings with device overrides
  const activeSettings = useMemo(() => {
    let override: DeviceAnimationOverride | undefined;
    if (deviceMode === 'mobile') override = validConfig.mobileOverrides;
    else if (deviceMode === 'tablet') override = validConfig.tabletOverrides;
    else override = validConfig.desktopOverrides;

    const baseEntrance = validConfig.entrance || {
      type: 'fade-up',
      duration: 700,
      delay: 0,
      easing: 'ease-out',
      trigger: 'on-scroll',
    };

    const type = override?.type || (validConfig.scroll?.enabled ? validConfig.scroll.type : baseEntrance.type) || 'fade-up';
    const duration = override?.duration || (validConfig.scroll?.enabled ? validConfig.scroll.duration : baseEntrance.duration) || 700;
    const delay = override?.delay ?? (validConfig.scroll?.enabled ? validConfig.scroll.delay : baseEntrance.delay) ?? 0;
    const easing = baseEntrance.easing || 'ease-out';
    const trigger = validConfig.scroll?.enabled ? 'on-scroll' : baseEntrance.trigger;

    return { type, duration, delay, easing, trigger };
  }, [validConfig, deviceMode]);

  // Reset or re-trigger when forcePlayKey changes in editor preview
  useEffect(() => {
    if (forcePlayKey !== undefined) {
      setIsInView(false);
      setHasAnimated(false);
      const timer = setTimeout(() => {
        setIsInView(true);
        setHasAnimated(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [forcePlayKey]);

  // IntersectionObserver for scroll trigger
  useEffect(() => {
    if (!isDeviceEnabled || activeSettings.type === 'none') {
      setIsInView(true);
      return;
    }

    if (activeSettings.trigger === 'on-load') {
      const timer = setTimeout(() => {
        setIsInView(true);
        setHasAnimated(true);
      }, activeSettings.delay || 50);
      return () => clearTimeout(timer);
    }

    // Scroll trigger with IntersectionObserver
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }

    const threshold = validConfig.scroll?.threshold ?? 0.15;
    const once = validConfig.scroll?.once !== false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            setHasAnimated(true);
            if (once) {
              observer.unobserve(entry.target);
            }
          } else if (!once && validConfig.scroll?.repeat) {
            setIsInView(false);
          }
        });
      },
      {
        threshold,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [isDeviceEnabled, activeSettings, validConfig.scroll]);

  // If disabled, render plain element
  if (!isDeviceEnabled || activeSettings.type === 'none') {
    return (
      <Component
        ref={containerRef}
        className={className}
        style={style}
        {...restProps}
      >
        {children}
      </Component>
    );
  }

  const presetCss = getPresetCss(activeSettings.type);

  // Compute animated style
  const animatedStyle: React.CSSProperties = {
    ...style,
    willChange: 'opacity, transform',
    animationDuration: `${activeSettings.duration}ms`,
    animationDelay: `${activeSettings.delay}ms`,
    animationTimingFunction: activeSettings.easing,
    animationFillMode: 'both',
  };

  if (isInView || hasAnimated) {
    animatedStyle.animationName = presetCss.keyframeName;
    animatedStyle.opacity = 1;
    animatedStyle.transform = 'none';
  } else {
    animatedStyle.opacity = presetCss.initialOpacity;
    animatedStyle.transform = presetCss.initialTransform;
  }

  // Hover effect classes
  let hoverClass = '';
  if (validConfig.hover?.enabled && validConfig.hover.type !== 'none') {
    if (validConfig.hover.type === 'lift') hoverClass = 'anim-hover-lift';
    else if (validConfig.hover.type === 'scale') hoverClass = 'anim-hover-scale';
    else if (validConfig.hover.type === 'glow') hoverClass = 'anim-hover-glow';
  }

  // Stagger custom property for child selectors
  const staggerStyles: React.CSSProperties = validConfig.staggerChildren
    ? ({
        '--stagger-step': `${validConfig.staggerChildren}ms`,
        ...animatedStyle,
      } as any)
    : animatedStyle;

  return (
    <Component
      ref={containerRef}
      data-adiosoft-animated={activeSettings.type}
      data-animation-trigger={activeSettings.trigger}
      className={`adiosoft-anim-wrapper ${hoverClass} ${className}`.trim()}
      style={staggerStyles}
      {...restProps}
    >
      {children}
    </Component>
  );
};
